import {makeAutoObservable} from 'mobx';
import OrganizationRepository from '../../../../data/repositories/OrganizationRepository';
import LibraryRepository from '../../../../data/repositories/LibraryRepository';
import MicroreelsRepository from '../../../../data/repositories/MicroreelsRepository';
import LearnerRepository from '../../../../data/repositories/LearnerRepository';
import {
  MicroreelGroup,
  getGroupReels,
} from '../../../../data/models/Microreels/MicroreelGroup';
import {LearnerListItem} from '../../../../data/models/Learners/Learner';
import {DashboardData} from '../../../../data/models/Workspace/Dashboard';

const GLANCE_LIST_SIZE = 3;

export interface HomeGroupsGlance {
  groups: MicroreelGroup[];
  microreelsCount: number;
}

// Each widget fetches on its own so the page can show per-widget preloaders
// instead of waiting for the slowest request.
class TapyblAIHomeStore {
  private organizationRepo = new OrganizationRepository();
  private libraryRepo = new LibraryRepository();
  private microreelsRepo = new MicroreelsRepository();
  private learnerRepo = new LearnerRepository();

  constructor() {
    makeAutoObservable(this);
  }

  public async getDashboard(
    organizationId: string,
  ): Promise<DashboardData | undefined> {
    const result =
      await this.organizationRepo.getOrganizationDashboardDetails(
        organizationId,
      );
    if (result.success && result.data) {
      return result.data;
    }
    return undefined;
  }

  public async getGlanceGroups(
    organizationId: string,
  ): Promise<HomeGroupsGlance> {
    const result = await this.microreelsRepo.getMicroreelsList(
      organizationId,
      0,
      100,
    );
    if (result.success && result.data) {
      return {
        microreelsCount: result.data.data.reduce(
          (sum, group) => sum + getGroupReels(group).length,
          0,
        ),
        groups: result.data.data.slice(0, GLANCE_LIST_SIZE),
      };
    }
    return {groups: [], microreelsCount: 0};
  }

  public async getGlanceLearners(
    organizationId: string,
  ): Promise<LearnerListItem[]> {
    const result = await this.learnerRepo.getMyLearners(
      0,
      organizationId,
      GLANCE_LIST_SIZE,
    );
    if (result.success && result.data) {
      return result.data.data.slice(0, GLANCE_LIST_SIZE);
    }
    return [];
  }

  public async getDocumentsCount(organizationId: string): Promise<number> {
    // pageSize 1 makes pagesCount equal the total amount of files
    const result = await this.libraryRepo.getMediaFiles(
      0,
      organizationId,
      1,
      '',
    );
    if (result.success && result.data) {
      return result.data.pagesCount;
    }
    return 0;
  }
}

export default TapyblAIHomeStore;
